import { useCallback, useSyncExternalStore } from 'react';
import {
  parseBinaryBody,
  type BodyEncoding,
} from '../electron-shared/utils/binary-body';

export type RequestBody = {
  requestBody?: string;
  responseBody?: string;
  originalRequestBody?: string;
};

type TransportBodyRequest = {
  id?: string;
  body?: string;
  bodyRef?: string;
  bodyContentType?: 'text' | 'binary' | 'multipart';
};

type TransportBodyResult = {
  body: string;
  bodyEncoding: BodyEncoding | undefined;
};

const bodyCache = new Map<string, RequestBody>();
const loadingPromises = new Map<string, Promise<RequestBody | null>>();
const subscribers = new Set<() => void>();

const largeBodyCache = new Map<string, string>();
const largeBodyLoadingPromises = new Map<string, Promise<string | null>>();

const MAX_CACHE_SIZE = 500;
const MAX_LARGE_BODY_CACHE_SIZE = 50;
const cacheAccessOrder: string[] = [];
const largeBodyCacheOrder: string[] = [];

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as unknown as { bodyCache: Map<string, RequestBody> }).bodyCache =
    bodyCache;
}

function notifySubscribers() {
  subscribers.forEach((callback) => callback());
}

function addToCache(requestId: string, bodies: RequestBody) {
  const existingIndex = cacheAccessOrder.indexOf(requestId);

  if (existingIndex !== -1) {
    cacheAccessOrder.splice(existingIndex, 1);
  }

  cacheAccessOrder.push(requestId);

  while (cacheAccessOrder.length > MAX_CACHE_SIZE) {
    const oldestId = cacheAccessOrder.shift();

    if (oldestId) {
      bodyCache.delete(oldestId);
    }
  }

  bodyCache.set(requestId, bodies);
  notifySubscribers();
}

async function fetchBodiesFromDb(
  requestId: string,
): Promise<RequestBody | null> {
  if (bodyCache.has(requestId)) {
    return bodyCache.get(requestId) || null;
  }

  if (loadingPromises.has(requestId)) {
    return loadingPromises.get(requestId) || null;
  }

  const promise = (async () => {
    try {
      if (!window.electronAPI?.bodyDb?.get) {
        return null;
      }

      const result = await window.electronAPI.bodyDb.get(requestId);

      if (!result.success || !result.data) {
        return null;
      }

      const bodies: RequestBody = {
        requestBody: result.data.requestBody ?? undefined,
        responseBody: result.data.responseBody ?? undefined,
        originalRequestBody: result.data.originalRequestBody ?? undefined,
      };

      addToCache(requestId, bodies);

      return bodies;
    } catch (error) {
      console.error(`Failed to fetch bodies for ${requestId}:`, error);

      return null;
    } finally {
      loadingPromises.delete(requestId);
    }
  })();

  loadingPromises.set(requestId, promise);

  return promise;
}

function cacheLargeBody(bodyRef: string, content: string): void {
  const existingIndex = largeBodyCacheOrder.indexOf(bodyRef);

  if (existingIndex !== -1) {
    largeBodyCacheOrder.splice(existingIndex, 1);
  }

  largeBodyCacheOrder.push(bodyRef);

  while (largeBodyCacheOrder.length > MAX_LARGE_BODY_CACHE_SIZE) {
    const oldestRef = largeBodyCacheOrder.shift();

    if (oldestRef) {
      largeBodyCache.delete(oldestRef);
    }
  }

  largeBodyCache.set(bodyRef, content);
  notifySubscribers();
}

async function fetchLargeBodyFromMainProcess(
  bodyRef: string,
): Promise<string | null> {
  if (largeBodyCache.has(bodyRef)) {
    return largeBodyCache.get(bodyRef) || null;
  }

  if (largeBodyLoadingPromises.has(bodyRef)) {
    return largeBodyLoadingPromises.get(bodyRef) || null;
  }

  const promise = (async () => {
    try {
      if (!window.electronAPI?.bodyStorage?.get) {
        return null;
      }

      const result = await window.electronAPI.bodyStorage.get(bodyRef);

      if (!result.success) {
        console.error(`Failed to fetch large body ${bodyRef}:`, result.error);

        return null;
      }

      const content = result.data ?? '';

      cacheLargeBody(bodyRef, content);

      return content;
    } catch (error) {
      console.error(`Failed to fetch large body ${bodyRef}:`, error);

      return null;
    } finally {
      largeBodyLoadingPromises.delete(bodyRef);
    }
  })();

  largeBodyLoadingPromises.set(bodyRef, promise);

  return promise;
}

async function fetchLargeBodyAsBinaryFromMainProcess(
  bodyRef: string,
): Promise<string | null> {
  const cacheKey = `binary:${bodyRef}`;

  if (largeBodyCache.has(cacheKey)) {
    return largeBodyCache.get(cacheKey) || null;
  }

  if (largeBodyLoadingPromises.has(cacheKey)) {
    return largeBodyLoadingPromises.get(cacheKey) || null;
  }

  const promise = (async () => {
    try {
      if (!window.electronAPI?.bodyStorage?.getBinary) {
        return null;
      }

      const result = await window.electronAPI.bodyStorage.getBinary(bodyRef);

      if (!result.success) {
        console.error(
          `Failed to fetch large body as binary ${bodyRef}:`,
          result.error,
        );

        return null;
      }

      const base64Content = result.data ?? '';

      cacheLargeBody(cacheKey, base64Content);

      return base64Content;
    } catch (error) {
      console.error(`Failed to fetch large body as binary ${bodyRef}:`, error);

      return null;
    } finally {
      largeBodyLoadingPromises.delete(cacheKey);
    }
  })();

  largeBodyLoadingPromises.set(cacheKey, promise);

  return promise;
}

function clearBodyCache() {
  bodyCache.clear();
  cacheAccessOrder.length = 0;
  largeBodyCache.clear();
  largeBodyCacheOrder.length = 0;
  notifySubscribers();
}

export function invalidateCacheEntry(requestId: string) {
  if (bodyCache.has(requestId)) {
    bodyCache.delete(requestId);
    const index = cacheAccessOrder.indexOf(requestId);

    if (index !== -1) {
      cacheAccessOrder.splice(index, 1);
    }

    notifySubscribers();
  }
}

export function useRequestBodies() {
  const cacheVersion = useSyncExternalStore(
    (callback) => {
      subscribers.add(callback);

      return () => subscribers.delete(callback);
    },
    () => bodyCache.size + largeBodyCache.size,
    () => 0,
  );

  const fetchBodies = useCallback(
    async (requestId: string): Promise<RequestBody | null> => {
      return fetchBodiesFromDb(requestId);
    },
    [],
  );

  const fetchLargeBody = useCallback(
    async (bodyRef: string): Promise<string | null> => {
      return fetchLargeBodyFromMainProcess(bodyRef);
    },
    [],
  );

  const fetchLargeBodyAsBinary = useCallback(
    async (bodyRef: string): Promise<string | null> => {
      return fetchLargeBodyAsBinaryFromMainProcess(bodyRef);
    },
    [],
  );

  const getLargeBody = useCallback((bodyRef: string): string | null => {
    return largeBodyCache.get(bodyRef) || null;
  }, []);

  const getBodies = useCallback((requestId: string): RequestBody | null => {
    return bodyCache.get(requestId) || null;
  }, []);

  const isLoading = useCallback((requestId: string): boolean => {
    return loadingPromises.has(requestId);
  }, []);

  const isLargeBodyLoading = useCallback((bodyRef: string): boolean => {
    return largeBodyLoadingPromises.has(bodyRef);
  }, []);

  const clearCache = useCallback(() => {
    clearBodyCache();
  }, []);

  const resolveRequestBodyForTransport = useCallback(
    async ({
      request,
      fallbackToBodyDb = false,
    }: {
      request: TransportBodyRequest;
      fallbackToBodyDb?: boolean;
    }): Promise<TransportBodyResult | null> => {
      const isBinaryBody = request.bodyContentType !== 'text';
      let body = request.body || '';
      let bodyEncoding: BodyEncoding | undefined;

      if (request.bodyRef) {
        if (isBinaryBody) {
          const base64Body = await fetchLargeBodyAsBinary(request.bodyRef);

          if (!base64Body) {
            return null;
          }

          body = base64Body;
          bodyEncoding = 'base64';
        } else {
          const largeBody = await fetchLargeBody(request.bodyRef);

          if (largeBody) {
            body = largeBody;
          }
        }
      }

      if (!body && fallbackToBodyDb && request.id) {
        const bodies = await fetchBodies(request.id);

        if (bodies?.requestBody) {
          body = bodies.requestBody;
        }
      }

      if (!bodyEncoding) {
        const parsed = parseBinaryBody(body);

        body = parsed.body;
        bodyEncoding = parsed.bodyEncoding;
      }

      return { body, bodyEncoding };
    },
    [fetchBodies, fetchLargeBody, fetchLargeBodyAsBinary],
  );

  return {
    fetchBodies,
    fetchLargeBody,
    fetchLargeBodyAsBinary,
    resolveRequestBodyForTransport,
    getLargeBody,
    getBodies,
    isLoading,
    isLargeBodyLoading,
    clearCache,
    cacheSize: cacheVersion,
  };
}
